import { connect } from 'react-redux';
import { useContext, useMemo } from 'react';
import { isBefore } from 'date-fns';

import { AgreementsContext } from 'KnmErknmPackage/ErkmAgreementsModule/AgreementsContext';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import {
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { AgreementStatusEnum } from 'APIPackage/KnmModule/enums/AgreementStatusEnum';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';

import styles from './AgreementsButtons.module.scss';
import { SignButton } from './Buttons/SignButton/SignButton';
import { SendToApproveButton } from './Buttons/SendToApproveButton/SendToApproveButton';
import { getIsDirtyAgreement } from './Buttons/SignButton/getIsDirtyAgreement';
import { WorkWithAgreementButton } from './Buttons/WorkWithAgreementButton/WorkWithAgreementButton';

interface IAgreementsButtonsProps {
  agreement: IErknmFormAgreement;
  agreementIndex?: number;
  name: string;
  areAllViolationCompleted: boolean;
  watchOnlyMain: boolean;
}

interface IAgreementsButtonsStoreProps {
  isSupervisor: boolean;
  isProsecutorOrSuperProsecutor: boolean;
  formRefKnm: IKnmFormRef | undefined;
  decisionFormRef: IDecisionFormRef | undefined;
}

const AgreementsButtonsWithoutStore = ({
  agreement,
  agreementIndex,
  isSupervisor,
  name,
  decisionFormRef,
  formRefKnm,
  areAllViolationCompleted,
  isProsecutorOrSuperProsecutor,
  watchOnlyMain,
}: IAgreementsButtonsProps & IAgreementsButtonsStoreProps) => {
  const { isDecision } = useContext(AgreementsContext);
  const formRef = isDecision ? decisionFormRef : formRefKnm;

  const {
    wasEditFiled: isDirtyBySigned,
    wasEditWithoutViolationsFixed: isDirtyBySignedWithoutViolationsFixed,
  } = getIsDirtyAgreement(formRef?.current?.getState()?.dirtyFields, agreementIndex, formRef);
  const isDirty = formRef?.current?.getState()?.dirty;

  const termOfExecutionAgreementFormated = parseDate({
    date: agreement.termOfExecutionAgreement || '',
    parseFormat: DATE_FORMAT_LONG_FNS,
  });

  const isBeforeTermOfExecutionAgreement = isBefore(new Date(), termOfExecutionAgreementFormated || 0);

  /**
   * Есть не подписанное изменение
   */
  const hasUnsignedChange = useMemo(() => {
    if (agreement.changes) {
      return !agreement.changes.every(change => change.signed);
    }

    return false;
  }, [agreement]);

  /**
   * Показывать кнопку подписать или нет
   */
  const isShowSignButton = useMemo(() => {
    switch (agreement.status) {
      case AgreementStatusEnum.APPROVED:
        return isSupervisor && !agreement.signed
          ? !!agreement.terminate || !!agreement.execution || !!hasUnsignedChange
          : isDirtyBySigned && !agreement.terminate;
      case AgreementStatusEnum.DRAFT:
      case AgreementStatusEnum.READY_TO_APPROVE:
        return isSupervisor ? (isDirtyBySigned && agreement.signed) || !agreement.signed : false;
      default:
        return false;
    }
  }, [agreement, isSupervisor, isDirtyBySigned, agreement.signed, hasUnsignedChange]);

  if (watchOnlyMain) return null;

  return (
    <div className={styles.Wrapper}>
      {isShowSignButton && (
        <SignButton
          agreement={agreement}
          isDirtyForm={!!isDirty}
          isDirtyBySigned={isDirtyBySigned}
          isDirtyBySignedWithoutViolationsFixed={isDirtyBySignedWithoutViolationsFixed}
        />
      )}
      {isSupervisor &&
        agreement.status === AgreementStatusEnum.READY_TO_APPROVE &&
        agreement.signed &&
        !isDirtyBySigned && <SendToApproveButton name={name} agreement={agreement} />}
      {agreement.signed &&
        (agreement.status === AgreementStatusEnum.APPROVED ||
          agreement.status === AgreementStatusEnum.PENDING_TERMINATION) &&
        !isDirtyBySigned &&
        (isSupervisor ||
          (isBeforeTermOfExecutionAgreement && isProsecutorOrSuperProsecutor && !agreement.terminate)) && (
          <WorkWithAgreementButton
            name={name}
            agreement={agreement}
            isDirtyBySigned={isDirtyBySigned}
            areAllViolationCompleted={areAllViolationCompleted}
            isBeforeTermOfExecutionAgreement={isBeforeTermOfExecutionAgreement}
          />
        )}
    </div>
  );
};

const mapStateToProps = (state: IRootState): IAgreementsButtonsStoreProps => ({
  isSupervisor: getUserRoleIsSupervisor(state),
  formRefKnm: getKnmFormRef(state),
  decisionFormRef: getDecisionFormRef(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
});

export const AgreementsButtons = connect(mapStateToProps)(AgreementsButtonsWithoutStore);
